import { Link } from "react-router-dom";
import {
  ArrowUpDown,
  BellRing,
  CheckCircle2,
  Container,
  Inbox,
  ShieldAlert,
  Wallet,
} from "lucide-react";
import type { Notification, NotificationKind } from "@/types";
import { cn } from "@/lib/utils";

/**
 * Shared notification-center row (NOT-00), used by both surfaces: the topbar
 * bell's dropdown panel and the full `/notifications` page. Keeping one row
 * component is what makes the two surfaces read as the same feature — icon,
 * kind tint, unread treatment and line-clamping are identical in each.
 *
 * Ported from `.design-ref/prototype/features/notifications/notification-item.tsx`
 * (next/link → react-router-dom Link; no other changes to markup or tokens).
 */

type KindMeta = {
  label: string;
  Icon: React.ElementType;
  /** Icon colour. */
  iconClass: string;
  /** Circular chip behind the icon. */
  chipClass: string;
};

export const NOTIFICATION_KIND_META: Record<NotificationKind, KindMeta> = {
  approval:   { label: "Approval",   Icon: CheckCircle2, iconClass: "text-warning", chipClass: "bg-warning-soft" },
  transfer:   { label: "Transfer",   Icon: ArrowUpDown,  iconClass: "text-primary", chipClass: "bg-primary-soft" },
  wallet:     { label: "Wallet",     Icon: Wallet,       iconClass: "text-ink",     chipClass: "bg-bg" },
  deployment: { label: "Deployment", Icon: Container,    iconClass: "text-primary", chipClass: "bg-primary-soft" },
  security:   { label: "Security",   Icon: ShieldAlert,  iconClass: "text-danger",  chipClass: "bg-danger-soft" },
  system:     { label: "System",     Icon: BellRing,     iconClass: "text-muted",   chipClass: "bg-bg" },
};

const FALLBACK_KIND: KindMeta = {
  label: "Notification",
  Icon: BellRing,
  iconClass: "text-muted",
  chipClass: "bg-bg",
};

export function NotificationItem({
  notification,
  onActivate,
  readOnly = false,
}: {
  notification: Notification;
  /** Fired after activation so the panel can close itself. */
  onActivate?: () => void;
  readOnly?: boolean;
}) {
  const meta = (notification.kind && NOTIFICATION_KIND_META[notification.kind]) || FALLBACK_KIND;
  const { Icon } = meta;
  // An unread approval is waiting on someone: its icon pulses, like the
  // live-status dot on alloy.build (same ping timing, approval tint).
  const needsAction = notification.kind === "approval" && !notification.read;

  const inner = (
    <>
      <span
        className={cn(
          "grid size-8 shrink-0 place-items-center rounded-full",
          meta.chipClass,
          needsAction && "notif-pulse ring-2 ring-warning-border"
        )}
      >
        <Icon aria-hidden className={cn("size-4", meta.iconClass)} />
      </span>

      <span className="min-w-0 flex-1">
        {/* On phones the timestamp stacks under the title instead of competing for
            the same row; from `sm` it returns to the right-hand edge. */}
        <span className="flex flex-col gap-0.5 sm:flex-row sm:items-baseline sm:gap-3">
          <span
            className={cn(
              "min-w-0 truncate text-sm text-ink",
              notification.read ? "font-medium" : "font-semibold"
            )}
          >
            {notification.title}
          </span>
          <span className="shrink-0 text-xs text-muted sm:ml-auto">{notification.timeAgo}</span>
        </span>
        <span className="mt-0.5 block text-sm leading-snug text-muted">{notification.message}</span>
      </span>

      {!notification.read && (
        <span aria-hidden className="mt-2.5 size-2 shrink-0 rounded-full bg-primary" />
      )}
    </>
  );

  const className = cn(
    "flex w-full items-start gap-3 px-4 py-3 text-left outline-none transition-colors",
    "focus-visible:bg-bg",
    notification.read ? "hover:bg-bg" : "bg-primary-soft hover:bg-primary-soft"
  );

  if (notification.href) {
    return (
      <Link
        to={notification.href}
        onClick={onActivate}
        className={className}
        data-testid="notification-item"
        data-read={notification.read ? "true" : "false"}
      >
        {inner}
      </Link>
    );
  }

  return (
    <button
      type="button"
      disabled={readOnly}
      onClick={onActivate}
      className={className}
      data-testid="notification-item"
      data-read={notification.read ? "true" : "false"}
    >
      {inner}
    </button>
  );
}

/** Shared empty state so the panel and the page never diverge. */
export function NotificationEmptyState({
  title = "You're all caught up",
  description = "Alerts about approvals, transfers, wallets and deployments will appear here.",
}: {
  title?: string;
  description?: string;
}) {
  return (
    <div className="px-4 py-12 text-center" data-testid="notification-empty">
      <span className="mx-auto grid size-10 place-items-center rounded-full bg-bg">
        <Inbox aria-hidden className="size-5 text-muted" />
      </span>
      <p className="mt-3 text-sm font-semibold text-ink">{title}</p>
      <p className="mx-auto mt-1 max-w-[30ch] text-xs leading-snug text-muted">{description}</p>
    </div>
  );
}
